import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Trophy, Star, Flame, BookOpen, Mic, MessageCircle,
  CheckCircle, Target, Award, BarChart3, TrendingUp, Lock, Zap,
  FileText, Layers, GraduationCap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CircleProgress = ({ percentage, size = 120, strokeWidth = 10, color = '#14b8a6', label, sublabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor"
          strokeWidth={strokeWidth} className="text-muted/20" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color}
          strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease-in-out' }} />
        <text x={size / 2} y={size / 2} textAnchor="middle" dy="0.35em"
          className="fill-foreground text-xl font-bold" style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
          {percentage}%
        </text>
      </svg>
      {label && <p className="font-semibold text-sm">{label}</p>}
      {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
    </div>
  );
};

const QUIZ_NAMES = {
  pflege_quiz: 'Pflege Training',
  menschen_a1_quiz: 'Menschen A1.1',
  menschen_a12_quiz: 'Menschen A1.2',
  cultural_quiz: 'Cultural Learning',
};

export default function MyProgress() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/progress/report`);
        setReport(res.data);
      } catch (err) {
        toast.error('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6" data-testid="my-progress-page">
        <div className="max-w-5xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-32 rounded-xl" />)}
          </div>
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  const courseColors = {
    menschen_a11: { color: '#3b82f6', bg: 'from-blue-500/10 to-blue-500/5', border: 'border-blue-500/20', link: '/menschen-a1' },
    menschen_a12: { color: '#6366f1', bg: 'from-indigo-500/10 to-indigo-500/5', border: 'border-indigo-500/20', link: '/menschen-a12' },
    pflege: { color: '#06b6d4', bg: 'from-cyan-500/10 to-cyan-500/5', border: 'border-cyan-500/20', link: '/pflege-training' },
  };

  const earned = report?.badges?.list?.filter(b => b.earned) || [];
  const locked = report?.badges?.list?.filter(b => !b.earned) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="my-progress-page">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center gap-4 px-6 py-4 max-w-5xl mx-auto">
          <Link to="/dashboard" className="p-2 hover:bg-muted rounded-lg transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-bold">My Progress</h1>
            <p className="text-sm text-muted-foreground">Your complete learning journey</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="overview-stats">
          <Card className="border-t-4 border-t-teal-500">
            <CardContent className="pt-5 text-center">
              <Target className="h-6 w-6 text-teal-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{report?.overview?.current_level || 'A1'}</p>
              <p className="text-xs text-muted-foreground">Level</p>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-amber-500">
            <CardContent className="pt-5 text-center">
              <Star className="h-6 w-6 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{(report?.overview?.total_xp || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total XP</p>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-orange-500">
            <CardContent className="pt-5 text-center">
              <Flame className="h-6 w-6 text-orange-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{report?.overview?.streak_days || 0}</p>
              <p className="text-xs text-muted-foreground">Day Streak</p>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-purple-500">
            <CardContent className="pt-5 text-center">
              <Award className="h-6 w-6 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{report?.badges?.earned || 0}/{report?.badges?.total || 0}</p>
              <p className="text-xs text-muted-foreground">Badges</p>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-emerald-500">
            <CardContent className="pt-5 text-center">
              <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{report?.overview?.total_completed || 0}</p>
              <p className="text-xs text-muted-foreground">Lessons Done</p>
            </CardContent>
          </Card>
        </div>

        {/* Overall Completion Ring */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-teal-500" /> Overall Completion</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center justify-around gap-8" data-testid="completion-rings">
              <CircleProgress percentage={report?.overview?.overall_completion || 0} size={140} color="#14b8a6" label="Overall" sublabel={`${report?.overview?.total_completed || 0} of ${report?.overview?.total_lessons || 0} lessons`} />
              {report?.courses && Object.entries(report.courses).map(([id, course]) => (
                <CircleProgress key={id} percentage={course.percentage || 0} size={120} color={courseColors[id]?.color || '#14b8a6'} label={course.name} sublabel={`${course.completed}/${course.total_lessons}`} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Course Breakdown */}
        <div className="grid md:grid-cols-3 gap-6" data-testid="course-breakdown">
          {report?.courses && Object.entries(report.courses).map(([id, course]) => {
            const style = courseColors[id] || {};
            return (
              <Link to={style.link || '#'} key={id}>
                <Card className={`cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 bg-gradient-to-br ${style.bg} ${style.border}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-semibold">{course.name}</p>
                        <p className="text-xs text-muted-foreground">{course.total_lessons} lessons</p>
                      </div>
                      <span className="text-2xl font-bold" style={{ color: style.color }}>{course.percentage}%</span>
                    </div>
                    <Progress value={course.percentage} className="h-2 mb-3" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{course.completed} completed</span>
                      <span>{course.total_lessons - course.completed} remaining</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Activity Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="activity-stats">
          <Card>
            <CardContent className="pt-5 text-center">
              <Mic className="h-5 w-5 text-teal-500 mx-auto mb-1" />
              <p className="text-xl font-bold">{report?.pronunciation?.total_attempts || 0}</p>
              <p className="text-xs text-muted-foreground">Pronunciations</p>
              {report?.pronunciation?.avg_score > 0 && <p className="text-xs text-teal-500 mt-1">Avg: {report.pronunciation.avg_score}%</p>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 text-center">
              <MessageCircle className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-bold">{report?.activity?.conversations || 0}</p>
              <p className="text-xs text-muted-foreground">AI Conversations</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 text-center">
              <Layers className="h-5 w-5 text-indigo-500 mx-auto mb-1" />
              <p className="text-xl font-bold">{report?.activity?.flashcards_reviewed || 0}</p>
              <p className="text-xs text-muted-foreground">Flashcards</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 text-center">
              <GraduationCap className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
              <p className="text-xl font-bold">{report?.activity?.quizzes_passed || 0}</p>
              <p className="text-xs text-muted-foreground">Quizzes Passed</p>
            </CardContent>
          </Card>
        </div>

        {/* Quiz Results */}
        {report?.quiz?.results?.length > 0 && (
          <Card data-testid="quiz-results-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-5 w-5 text-emerald-500" /> Quiz Results
                {report.quiz.avg_score > 0 && <Badge variant="secondary" className="ml-auto">Avg: {report.quiz.avg_score}%</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.quiz.results.map(qr => (
                <div key={qr.quiz_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${qr.percentage >= 80 ? 'bg-emerald-500/10' : qr.percentage >= 60 ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                    <span className={`text-sm font-bold ${qr.percentage >= 80 ? 'text-emerald-500' : qr.percentage >= 60 ? 'text-blue-500' : 'text-amber-500'}`}>{qr.percentage}%</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{QUIZ_NAMES[qr.quiz_id] || qr.quiz_id}</p>
                    <p className="text-xs text-muted-foreground">{qr.correct}/{qr.total} correct</p>
                  </div>
                  <Progress value={qr.percentage} className="w-24 h-2" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Pronunciation & Listening */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card data-testid="pronunciation-stats">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mic className="h-5 w-5 text-teal-500" /> Pronunciation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold text-teal-500">{report?.pronunciation?.total_attempts || 0}</p>
                  <p className="text-xs text-muted-foreground">Recordings</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold text-blue-500">{report?.pronunciation?.avg_score || 0}%</p>
                  <p className="text-xs text-muted-foreground">Average</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold text-emerald-500">{report?.pronunciation?.best_score || 0}%</p>
                  <p className="text-xs text-muted-foreground">Best Score</p>
                </div>
              </div>
              <Link to="/pronunciation-history" className="text-sm text-teal-500 hover:underline flex items-center gap-1">
                View full history <BarChart3 className="h-3 w-3" />
              </Link>
            </CardContent>
          </Card>

          {/* Exam Simulation Results */}
          <Card data-testid="exam-stats">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <GraduationCap className="h-5 w-5 text-indigo-500" /> Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Lessons Completed</span>
                </div>
                <span className="font-bold">{report?.overview?.total_completed || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm">Quizzes Taken</span>
                </div>
                <span className="font-bold">{report?.quiz?.total_taken || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">Total XP Earned</span>
                </div>
                <span className="font-bold text-amber-500">{(report?.overview?.total_xp || 0).toLocaleString()}</span>
              </div>
              <Link to="/exam-simulation" className="text-sm text-indigo-500 hover:underline flex items-center gap-1">
                Take an exam <GraduationCap className="h-3 w-3" />
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* EARNED BADGES */}
        <Card data-testid="earned-badges-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="h-5 w-5 text-amber-500" /> Earned Badges ({earned.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {earned.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {earned.map(b => (
                  <div key={b.badge_id} className="text-center p-3 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5" data-testid={`earned-badge-${b.badge_id}`}>
                    <span className="text-3xl block mb-1">{b.icon}</span>
                    <p className="text-xs font-semibold">{b.name_en}</p>
                    <p className="text-[10px] text-muted-foreground">{b.description_en}</p>
                    {b.earned_at && <p className="text-[10px] text-amber-500 mt-1">{new Date(b.earned_at).toLocaleDateString()}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Complete lessons and quizzes to earn your first badge!</p>
            )}
          </CardContent>
        </Card>

        {/* LOCKED BADGES WITH PROGRESS */}
        {locked.length > 0 && (
          <Card data-testid="locked-badges-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Lock className="h-5 w-5 text-muted-foreground" /> Locked Badges ({locked.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {locked.map(b => (
                  <div key={b.badge_id} className="flex items-center gap-3 p-3 rounded-xl border border-border opacity-70 hover:opacity-90 transition-opacity" data-testid={`locked-badge-${b.badge_id}`}>
                    <div className="relative flex-shrink-0">
                      <span className="text-3xl grayscale block">{b.icon}</span>
                      <div className="absolute -bottom-1 -right-1 bg-slate-600 rounded-full p-0.5">
                        <Lock className="h-2.5 w-2.5 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{b.name_en}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{b.description_en}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Progress value={b.progress_pct} className="h-1.5 flex-1" />
                        <span className="text-[10px] font-medium text-muted-foreground whitespace-nowrap">
                          {b.current_progress}/{b.requirement_value}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {b.requirement_type === 'lessons_completed' && `Complete ${b.requirement_value} lessons`}
                        {b.requirement_type === 'points' && `Earn ${b.requirement_value} XP`}
                        {b.requirement_type === 'streak' && `${b.requirement_value}-day streak`}
                        {b.requirement_type === 'pronunciations' && `Record ${b.requirement_value} pronunciations`}
                        {b.requirement_type === 'conversations' && `Have ${b.requirement_value} AI conversations`}
                        {b.requirement_type === 'quizzes_passed' && `Pass ${b.requirement_value} quizzes`}
                        {b.requirement_type === 'flashcards_reviewed' && `Review ${b.requirement_value} flashcards`}
                        {b.requirement_type === 'pflege_completed' && 'Complete Pflege module'}
                        {b.requirement_type === 'level' && 'Complete all A1 lessons'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Link to all badges */}
        <div className="text-center">
          <Link to="/achievements" className="text-sm text-amber-500 hover:underline inline-flex items-center gap-1">
            View all badges & leaderboard <Award className="h-3 w-3" />
          </Link>
        </div>
      </main>
    </div>
  );
}
